// Nachweise der Inventur: Zähllisten für das Zählen auf Papier, die Inventurliste zum Stichtag (A4 und CSV für die
// Jahresabschlussakte), das Zählprotokoll (wer hat gezählt, wer freigegeben). Dazu der Stand der permanenten Inventur
// und die Kennzahlen der Startseite.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { COUNT_KINDS, COUNT_STATUSES, READ, zItemRef, zLocationRef, zPerson } from './common'

/** Kopf aller Drucke: wer, wo, was, wann – und der Stand der Inventur. */
export const zRecordHeader = z.object({
  id: zId,
  number: z.string().nullable(),
  name: z.string(),
  kind: z.enum(COUNT_KINDS),
  kindLabel: z.string(),
  status: z.enum(COUNT_STATUSES),
  statusLabel: z.string(),
  tenantName: z.string(),
  location: zLocationRef,
  keyDate: z.string(),
  blind: z.boolean(),
  scopeText: z.string(),
  startedAt: z.date().nullable(),
  approvedBy: zPerson.nullable(),
  approvedAt: z.date().nullable(),
  /** Demo-Betrieb: Der Druck trägt „DEMO“ */
  demo: z.boolean(),
})

export const countPrintSheets = defineContract({
  name: 'stocktaking.count.printSheets',
  title: 'Zähllisten drucken',
  description: 'Zähllisten je Lagerplatz zum Zählen auf Papier und späteren Erfassen.',
  llm: {
    description:
      'Liefert die Zähllisten einer gestarteten Inventur als Druckvorlage: je Lagerplatz ein Block. Ohne ' +
      'Blindzählung mit den erwarteten Artikeln und dem Buchbestand, bei einer Blindzählung mit leeren Zeilen. ' +
      'Die gezählten Mengen erfasst danach ein Mensch in der Zähloberfläche. Ändert nichts.',
    keywords: ['Zählliste', 'Zählblatt', 'drucken', 'Papier'],
  },
  input: z.strictObject({ id: zId, zone: z.string().trim().max(60).optional() }),
  output: z.object({
    header: zRecordHeader,
    bins: z.array(
      z.object({
        id: zId,
        code: z.string(),
        name: z.string().nullable(),
        zone: z.string(),
        assignedTo: z.string().nullable(),
        /** nur ohne Blindzählung */
        lines: z
          .array(z.object({ sku: z.string(), name: z.string(), unit: z.string(), baseUnit: z.string(), lotNo: z.string(), bestBefore: z.string().nullable(), bookQty: z.int() }))
          .optional(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
})

export const zInventoryRow = z.object({
  /** Position der Inventur; null = nach der Zählung des Platzes zugebucht */
  lineId: zId.nullable(),
  sku: z.string(),
  name: z.string(),
  binCode: z.string(),
  lotNo: z.string(),
  bestBefore: z.string().nullable(),
  /** Bestand zum Stichtag in Tausendstel der Basiseinheit: die Zählung, fortgeschrieben bzw. zurückgerechnet auf den Stichtag */
  stockQty: z.int(),
  baseUnit: z.string(),
  /** dieselbe Menge im Lagergebinde: „46 Ka + 12 Fl“ */
  stockText: z.string(),
  /** körperlich gezählte Menge – weicht ab, wenn zwischen Zählung und Stichtag gebucht wurde */
  countedQty: z.int().nullable(),
  countedBy: z.string().nullable(),
  countedAt: z.date().nullable(),
  /** Wert zum gleitenden Durchschnitt in Cent – nur mit „Einstandspreise sehen“ */
  valueCents: z.int().optional(),
})

export const countInventoryList = defineContract({
  name: 'stocktaking.count.inventoryList',
  title: 'Inventurliste zum Stichtag',
  description: 'Bestand je Lagerplatz und Artikel zum Stichtag mit Menge, Einheit, Wert, Zähler und Zählzeit.',
  llm: {
    description:
      'Liefert die Inventurliste einer Inventur: je Position Artikel, Lagerplatz, Bestand zum Stichtag ' +
      '(`stockQty`, Tausendstel der Basiseinheit), die gezählte Menge, wer wann gezählt hat und – nur mit ' +
      '„Einstandspreise sehen“ – den Wert in Cent zum gleitenden Durchschnitt. `final` ist erst nach dem Buchen wahr; ' +
      'vorher ist die Liste vorläufig. Über Abwertungen (Niederstwert) entscheidet die Steuerberatung – die ' +
      'Liste nennt nur den Einstandswert. Ändert nichts.',
    keywords: ['Inventurliste', 'Inventar', 'Jahresabschluss', 'Lagerwert', 'Bestandsliste', 'Stichtag'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    header: zRecordHeader,
    /** gebucht bzw. abgeschlossen – sonst vorläufig */
    final: z.boolean(),
    rows: z.array(zInventoryRow),
    totalValueCents: z.int().optional(),
    /** Bewertungsverfahren im Klartext */
    valuation: z.string(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
  fieldPermissions: { 'rows.*.valueCents': PERMISSION_COSTS_READ, totalValueCents: PERMISSION_COSTS_READ },
})

export const countProtocol = defineContract({
  name: 'stocktaking.count.protocol',
  title: 'Zählprotokoll',
  description: 'Wer wann welchen Platz gezählt hat, welche Positionen nachgezählt wurden, wer freigegeben hat.',
  llm: {
    description:
      'Liefert das Zählprotokoll einer Inventur: die Zählenden mit der Zahl ihrer Plätze und Positionen, jede ' +
      'eingereichte Zählliste (Nummer, Platz, Person, Zeit), die Nachzählungen mit Erst- und Zweitzählung, die ' +
      'Freigabe und die Buchung. Verwenden für „Wer hat Gang 3 gezählt?“, „Wer hat die Inventur freigegeben?“. ' +
      'Ändert nichts.',
    keywords: ['Zählprotokoll', 'Protokoll', 'wer hat gezählt', 'Freigabe', 'Nachweis'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    header: zRecordHeader,
    createdBy: zPerson.nullable(),
    counters: z.array(z.object({ name: z.string(), bins: z.int(), positions: z.int(), firstAt: z.date().nullable(), lastAt: z.date().nullable() })),
    lists: z.array(
      z.object({
        listNo: z.int(),
        binCode: z.string(),
        mode: z.enum(['bin', 'recount']),
        by: z.string().nullable(),
        countedAt: z.date(),
        positions: z.int(),
        emptyConfirmed: z.boolean(),
        note: z.string().nullable(),
      }),
    ),
    recounts: z.array(
      z.object({
        binCode: z.string(),
        sku: z.string(),
        name: z.string(),
        lotNo: z.string(),
        baseUnit: z.string(),
        state: z.enum(['requested', 'done']),
        requestedBy: z.string().nullable(),
        /** die Zählungen der Position – nur, wenn der Nutzer Mengen dieser Inventur sehen darf */
        counts: z.array(z.object({ round: z.int(), countedQty: z.int(), by: z.string().nullable(), at: z.date() })).optional(),
      }),
    ),
    posting: z
      .object({
        number: z.string().nullable(),
        date: z.string(),
        postedLines: z.int(),
        /** Saldo der gebuchten Differenzen in Cent */
        netValueCents: z.int().optional(),
      })
      .nullable(),
    cancelReason: z.string().nullable(),
    valuation: z.string(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
  fieldPermissions: { 'posting.netValueCents': PERMISSION_COSTS_READ },
})

// ---- permanente Inventur: Stand im Geschäftsjahr --------------------------------------------------------------

export const zRollingDue = z.object({
  item: zItemRef,
  /** Positionen des Artikels mit Bestand am Standort – und wie viele davon im Geschäftsjahr gezählt sind */
  positions: z.int(),
  positionsCounted: z.int(),
  binCodes: z.array(z.string()),
  lastCountedAt: z.date().nullable(),
})

export const rollingProgress = defineContract({
  name: 'stocktaking.rolling.progress',
  title: 'Stand der permanenten Inventur',
  description: 'Welche Artikel eines Standorts im Geschäftsjahr schon gezählt sind und welche noch ausstehen.',
  llm: {
    description:
      'Zeigt für einen Standort und ein Geschäftsjahr (Kalenderjahr, Standard: das laufende), wie weit die permanente ' +
      'Inventur ist: Artikel mit Bestand, davon im Jahr körperlich gezählt (jede Position mit Bestand mindestens ' +
      'einmal), `coverageBp` in Basispunkten (10000 = alles gezählt) und `due` – die Artikel, die noch ausstehen, ' +
      'längste Wartezeit zuerst. Verwenden für „Was muss dieses Jahr noch gezählt werden?“. Eine Teilzählung planen ' +
      'mit stocktaking.count.plan (`kind: rolling`). Ändert nichts.',
    keywords: ['permanente Inventur', 'rollierend', 'noch nicht gezählt', 'fällig', 'Geschäftsjahr', 'Zählfortschritt'],
  },
  input: z.strictObject({
    locationId: zId,
    year: z.int().min(2000).max(2100).optional(),
    /** höchstens so viele fällige Artikel liefern */
    limit: z.int().min(1).max(500).default(200),
  }),
  output: z.object({
    location: zLocationRef,
    year: z.int(),
    from: z.string(),
    to: z.string(),
    items: z.int(),
    itemsCounted: z.int(),
    positions: z.int(),
    positionsCounted: z.int(),
    coverageBp: z.int(),
    dueCount: z.int(),
    due: z.array(zRollingDue),
    /** Zählungen des Jahres an diesem Standort */
    counts: z.array(z.object({ id: zId, number: z.string().nullable(), name: z.string(), kindLabel: z.string(), status: z.enum(COUNT_STATUSES), statusLabel: z.string(), keyDate: z.string() })),
  }),
  permission: READ,
  risk: 'read',
  scope: (input) => [input.locationId],
})

// ---- Startseite ---------------------------------------------------------------------------------------------------

export const overviewRunning = defineContract({
  name: 'stocktaking.overview.running',
  title: 'Laufende Inventuren',
  description: 'Inventuren, die geplant sind, gezählt oder geprüft werden – mit Fortschritt.',
  llm: {
    description:
      'Liefert die laufenden Inventuren der Standorte des Nutzers (Geplant, Zählung läuft, In Prüfung) mit ' +
      'Fortschritt und offenen Nachzählungen; `count` ist ihre Zahl. Für die Startseite. Ändert nichts.',
    keywords: ['laufende Inventur', 'Zählung offen'],
  },
  input: z.strictObject({}),
  output: z.object({
    count: z.int(),
    rows: z.array(
      z.object({
        id: zId,
        name: z.string(),
        number: z.string().nullable(),
        locationCode: z.string(),
        status: z.enum(COUNT_STATUSES),
        statusLabel: z.string(),
        binsTotal: z.int(),
        binsCounted: z.int(),
        recountsOpen: z.int(),
        /** der Nutzer darf hier zählen */
        canCount: z.boolean(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const overviewRollingDue = defineContract({
  name: 'stocktaking.overview.rollingDue',
  title: 'Permanente Inventur: fällige Artikel',
  description: 'Je Standort: wie viele Artikel im Geschäftsjahr noch nicht gezählt sind.',
  llm: {
    description:
      'Liefert je Standort des Nutzers die Zahl der Artikel mit Bestand, die im laufenden Geschäftsjahr noch nicht ' +
      'körperlich gezählt wurden, und die Abdeckung in Basispunkten; `count` ist die Summe der fälligen Artikel. Für ' +
      'die Startseite; Einzelheiten mit stocktaking.rolling.progress. Ändert nichts.',
    keywords: ['permanente Inventur', 'fällig', 'noch nicht gezählt'],
  },
  input: z.strictObject({}),
  output: z.object({
    count: z.int(),
    year: z.int(),
    rows: z.array(z.object({ locationId: zId, locationCode: z.string(), locationName: z.string(), items: z.int(), dueItems: z.int(), coverageBp: z.int() })),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})
